import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Params } from '@angular/router';
import {
  CheckInFailure,
  CheckInFeatureActionTypes,
} from '@frontend/check-in-kiosk/feature/check-in/src';
import {
  CheckOutFailure,
  CheckOutFeatureActionTypes,
} from '@frontend/check-in-kiosk/feature/check-out/src';
import {
  FailureModalComponent,
  SuccessModalComponent,
} from '@frontend/check-in-kiosk/ui/src/';
import { PhAuthService } from '@frontend/common/ph-auth';
import { getQueryParams, getUrl, Go } from '@frontend/common/ph-router-store';
import { StaffTypeId } from '@frontend/common/util';
import {
  AddDepartment,
  DepartmentApiActionTypes,
  DepartmentWsActionTypes,
  GlobalRemoveDepartmentSuccessWs,
  LoadDepartmentApi,
  LoadDepartmentSuccessApi,
} from '@frontend/shared/department/data-access/src';
import {
  AddKiosk,
  getKiosks,
  GlobalRemoveKioskSuccessWs,
  GlobalUpdateKioskSuccessWs,
  KioskApiActionTypes,
  KioskWsActionTypes,
  LoadKioskSuccessApi,
  UpdateKiosk,
} from '@frontend/shared/kiosk/data-access/src';
import {
  AddSite,
  GlobalRemoveSiteSuccessWs,
  GlobalUpdateSiteSuccessWs,
  LoadSiteApi,
  LoadSiteSuccessApi,
  SiteApiActionTypes,
  SiteWsActionTypes,
  UpdateSite,
} from '@frontend/shared/site/data-access/src';
import {
  AddStaff,
  DeleteStaff,
  GlobalAddStaffDepartmentWs,
  LoadStaffs,
  LoadStaffsApi,
  LoadStaffsSuccessApi,
  StaffApiActionTypes,
  StaffWsActionTypes,
} from '@frontend/shared/staff/data-access';
import {
  GlobalDeactivateWorkflowSuccessWs,
  GlobalRemoveWorkflowSuccessWs,
  LoadWorkflows,
  LoadWorkflowsApi,
  LoadWorkflowsSuccessApi,
  WorkflowApiActionTypes,
  WorkflowWsActionTypes,
} from '@frontend/shared/workflow/data-access/src';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { merge } from 'rxjs';
import {
  debounceTime,
  delayWhen,
  filter,
  first,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  AppActionTypes,
  Misconfiguration,
  StartLoading,
  StopLoading,
} from './actions/app.actions';
import {
  getKioskDepartmentId,
  getKioskSiteId,
  getKioskWorkflowIds,
} from './app.selectors';

@Injectable()
export class AppEffects {
  onLoadedKiosk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KioskApiActionTypes.LoadKioskSuccessApi),
      mergeMap(({ payload: { kiosk } }: LoadKioskSuccessApi) => [
        new AddKiosk({ kiosk }),
        new LoadDepartmentApi({ id: kiosk.department_id.toString() }),
        new LoadStaffsApi({
          params: {
            department_id: kiosk.department_id.toString(),
            staff_type_id: [
              StaffTypeId.Advanced_Practice_Practitioner,
              StaffTypeId.Anesthesiologist,
              StaffTypeId.Nurse_Practitioner,
              StaffTypeId.Physician,
              StaffTypeId.Physician_Assistant,
              StaffTypeId.Provider,
              StaffTypeId.Surgeon,
              StaffTypeId.Radiologist,
              StaffTypeId.ENT,
              StaffTypeId.Fellow_Resident,
              StaffTypeId.CRNA,
            ],
          },
        }),
        new LoadWorkflowsApi({
          params: {
            id: [
              ...new Set([...kiosk.checkin_workflow_ids, ...kiosk.checkout_workflow_ids]),
            ],
            is_activated: true,
            is_disabled: false,
          },
        }),
      ])
    )
  );

  onLoadedWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowApiActionTypes.LoadWorkflowsSuccessApi),
      map(({ payload: { workflows } }: LoadWorkflowsSuccessApi) => {
        if (workflows.length) {
          return new LoadWorkflows({ workflows });
        }
        return new Misconfiguration();
      })
    )
  );

  onLoadedStaff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StaffApiActionTypes.LoadStaffsSuccessApi),
      map(({ payload: { staffs } }: LoadStaffsSuccessApi) => new LoadStaffs({ staffs }))
    )
  );

  onLoadedDepartment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DepartmentApiActionTypes.LoadDepartmentSuccessApi),
      mergeMap(({ payload: { department } }: LoadDepartmentSuccessApi) => [
        new AddDepartment({ department }),
        new LoadSiteApi({ id: department.site_id.toString() }),
      ])
    )
  );

  onLoadedSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SiteApiActionTypes.LoadSiteSuccessApi),
      mergeMap(({ payload: { site } }: LoadSiteSuccessApi) => [new AddSite({ site })])
    )
  );

  successModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CheckInFeatureActionTypes.CheckInSuccess,
        CheckOutFeatureActionTypes.CheckOutSuccess
      ),
      mergeMap(() =>
        this.dialog
          .open(SuccessModalComponent, {
            panelClass: 'success',
            minHeight: '152px',
            width: '752px',
            position: { top: '24px' },
            closeOnNavigation: true,
          })
          .afterClosed()
      ),
      map(() => new Go({ path: ['welcome'], extras: { queryParamsHandling: 'preserve' } }))
    )
  );

  failureModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CheckInFeatureActionTypes.CheckInFailure,
          CheckOutFeatureActionTypes.CheckOutFailure
        ),
        tap(({ payload: { errorCode } }: CheckInFailure | CheckOutFailure) =>
          this.dialog.open(FailureModalComponent, {
            panelClass: 'failure',
            minHeight: '152px',
            width: '752px',
            position: { top: '24px' },
            closeOnNavigation: true,
            data: { errorCode },
          })
        )
      ),
    { dispatch: false }
  );

  startLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckInFeatureActionTypes.CheckIn, CheckOutFeatureActionTypes.CheckOut),
      map(() => new StartLoading())
    )
  );

  stopLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CheckInFeatureActionTypes.CheckInSuccess,
        CheckInFeatureActionTypes.CheckInFailure,
        CheckOutFeatureActionTypes.CheckOutSuccess,
        CheckOutFeatureActionTypes.CheckOutFailure
      ),
      map(() => new StopLoading())
    )
  );

  updateKioskWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KioskWsActionTypes.GlobalUpdateKioskSuccessWs),
      map(({ payload: { kiosk } }: GlobalUpdateKioskSuccessWs) => {
        const changes = {
          checkin_workflow_ids: kiosk.checkin_workflow_ids,
          checkout_workflow_ids: kiosk.checkout_workflow_ids,
        };
        return new UpdateKiosk({ kiosk: { id: kiosk.id, changes } });
      })
    )
  );

  updateKiosk$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KioskWsActionTypes.GlobalUpdateKioskSuccessWs),
      delayWhen(() =>
        this.store.pipe(
          select(getUrl),
          filter((url) => url.indexOf('/welcome') !== -1)
        )
      ),
      map(
        ({ payload: { kiosk } }: GlobalUpdateKioskSuccessWs) =>
          new UpdateKiosk({ kiosk: { id: kiosk.id, changes: kiosk } })
      )
    )
  );

  updateSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SiteWsActionTypes.GlobalUpdateSiteSuccessWs),
      map(
        ({ payload: { site } }: GlobalUpdateSiteSuccessWs) =>
          new UpdateSite({ site: { id: site.id, changes: site } })
      )
    )
  );

  StaffTableGlobalSuccessfulOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        StaffWsActionTypes.GlobalCreateStaffSuccessWs,
        StaffWsActionTypes.GlobalUpdateStaffSuccessWs,
        StaffWsActionTypes.GlobalRemoveStaffSuccessWs
      ),
      debounceTime(2000),
      withLatestFrom(this.store.select(getKioskDepartmentId)),
      map(
        ([_, department_id]) =>
          new LoadStaffsApi({
            params: {
              department_id: department_id.toString(),
              staff_type_id: StaffTypeId.Provider,
            },
          })
      )
    )
  );

  onAddStaffDepartment = createEffect(() =>
    this.actions$.pipe(
      ofType(StaffWsActionTypes.GlobalAddStaffDepartmentWs),
      withLatestFrom(this.store.select(getKioskDepartmentId)),
      filter(
        ([
          {
            payload: { staff },
          },
          department_id,
        ]: [any, number]) =>
          staff.department_id === department_id &&
          staff.staff_type_id === StaffTypeId.Provider
      ),
      map(([staff]) => staff),
      map(
        ({ payload: { staff } }: GlobalAddStaffDepartmentWs) =>
          new AddStaff({
            staff,
          })
      )
    )
  );

  onRemoveStaffDepartment = createEffect(() =>
    this.actions$.pipe(
      ofType(StaffWsActionTypes.GlobalRemoveStaffDepartmentWs),
      withLatestFrom(this.store.select(getKioskDepartmentId)),
      filter(
        ([
          {
            payload: { staff },
          },
          department_id,
        ]: [any, number]) =>
          staff.department_id === department_id &&
          staff.staff_type_id === StaffTypeId.Provider
      ),
      map(([staff]) => staff),
      map(
        ({ payload: { staff } }: any) =>
          new DeleteStaff({
            id: staff.staff_id.toString(),
          })
      )
    )
  );

  wrongConfigModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.Misconfiguration),
      first(),
      tap(() =>
        this.dialog.open(FailureModalComponent, {
          panelClass: 'failure',
          minHeight: '152px',
          width: '752px',
          position: { top: '24px' },
          closeOnNavigation: false,
          disableClose: true,
          data: { errorCode: 'APP_MISCONFIGURATION' },
        })
      ),
      map(() => new Go({ path: ['welcome'], extras: { queryParamsHandling: 'preserve' } }))
    )
  );

  kioskDeleted$ = this.actions$.pipe(
    ofType(KioskWsActionTypes.GlobalRemoveKioskSuccessWs),
    withLatestFrom(this.store.pipe(select(getQueryParams))),
    filter(
      ([action, params]: [GlobalRemoveKioskSuccessWs, Params]) =>
        params.id === action.payload.id.toString()
    )
  );

  workflowDeleted$ = this.actions$.pipe(
    ofType(WorkflowWsActionTypes.GlobalRemoveWorkflowSuccessWs),
    withLatestFrom(this.store.pipe(select(getKioskWorkflowIds))),
    filter(
      ([action, workflowIds]: [GlobalRemoveWorkflowSuccessWs, number[]]) =>
        workflowIds.indexOf(action.payload.id) >= 0
    )
  );

  workflowDisabled$ = this.actions$.pipe(
    ofType(WorkflowWsActionTypes.GlobalDeactivateWorkflowSuccessWs),
    withLatestFrom(this.store.pipe(select(getKioskWorkflowIds))),
    filter(
      ([action, workflowIds]: [GlobalDeactivateWorkflowSuccessWs, number[]]) =>
        workflowIds.indexOf(action.payload.id) >= 0
    )
  );

  departmentDeleted$ = this.actions$.pipe(
    ofType(DepartmentWsActionTypes.GlobalRemoveDepartmentSuccessWs),
    withLatestFrom(this.store.pipe(select(getKioskDepartmentId))),
    filter(
      ([action, departmentId]: [GlobalRemoveDepartmentSuccessWs, number]) =>
        action.payload.id === departmentId
    )
  );

  siteDeleted$ = this.actions$.pipe(
    ofType(SiteWsActionTypes.GlobalRemoveSiteSuccessWs),
    withLatestFrom(this.store.pipe(select(getKioskSiteId))),
    filter(
      ([action, siteId]: [GlobalRemoveSiteSuccessWs, number]) =>
        action.payload.id === siteId
    )
  );

  workflowDisabledOrDeleted$ = createEffect(() =>
    merge(this.workflowDeleted$, this.workflowDisabled$).pipe(
      withLatestFrom(this.store.pipe(select(getKiosks))),
      map(([_, kiosks]) => kiosks[0]),
      map(
        (kiosk) =>
          new LoadWorkflowsApi({
            params: {
              id: [
                ...new Set([
                  ...kiosk.checkin_workflow_ids,
                  ...kiosk.checkout_workflow_ids,
                ]),
              ],
              is_activated: true,
              is_disabled: false,
            },
          })
      )
    )
  );

  misconfiguration$ = createEffect(() =>
    merge(this.kioskDeleted$, this.departmentDeleted$, this.siteDeleted$).pipe(
      map(() => new Misconfiguration())
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private dialog: MatDialog,
    private phAuthSrv: PhAuthService
  ) {}
}